import { useMemo } from "react";
import { useGlobalContext } from "context/global";
import { calculateBowlsPrice, calculateTotalBoxPrice, calculateTotalNoWeightPrice, getHighestBoxPrice } from "utils/cart";
import { useSelector } from "react-redux";

/**
 * This hook calculates prices and discounts for the cart, based on the current box and products.
 *
 * @param {Object} [currentBox] - The current box object containing details about the box being used. If not provided, the active box from the cart will be used.
 * @param {Array} [products] - An array of product objects currently in the cart. Each product object should contain details such as price, quantity. If not provided, the products from the active cart will be used.
 * @param {number} [totalWeight] - The total weight of the products in the cart. This is used to calculate shipping costs or weight-based discounts. If not provided, the total weight will be calculated from the products in the active cart.
 * @param {boolean} [calcFirstOrderDiscount] - Whether to calculate the first order discount. This is used to determine if the first order discount should be applied. If not provided, the first order discount will be calculated based on the cart settings.
 *
 * @returns {Object} An object containing the calculated prices, discounts, and any other relevant summary information for the cart.
 **/
const useCartSummary = ({ currentBox = null, products = [], totalWeight, calcFirstOrderDiscount } = {}) => {
	const {
		cartSettings: { boxes, shippingCost, freeShippingThreshold, subscription }, isOneTimeCollection
	} = useGlobalContext();
	const { activeBox, items, allGiftCards } = useSelector(state => state.cart);

	if (typeof calcFirstOrderDiscount === "undefined") {
		calcFirstOrderDiscount = !isOneTimeCollection && !allGiftCards;
	}

	const box = currentBox || activeBox;

	const cartItems = products.length ? products : Object.values(items);
	const discountPercentage = subscription?.discountPercentage;

	const totalBowlsPrice = useMemo(() => calculateBowlsPrice(cartItems), [cartItems, box]);
	const totalBoxPrice = useMemo(() => calculateTotalBoxPrice(cartItems, box?.discount), [cartItems, box]);
	const totalPriceNoWeight = useMemo(() => calculateTotalNoWeightPrice(cartItems), [cartItems, box]);

	const calculateFirstOrderDiscount = () => {
		if (discountPercentage && calcFirstOrderDiscount) {
			const discountableTotal = totalBoxPrice - totalPriceNoWeight;
			return Math.ceil((discountPercentage / 100 * discountableTotal) * 100) / 100;
		}
		return 0;
	};

	const firstOrderDiscount = calculateFirstOrderDiscount();

	const totalDiscounted = totalBoxPrice - (discountPercentage ? firstOrderDiscount : 0);

	const showFreeShipping = box?.size?.value >= freeShippingThreshold;

	const savings = totalBowlsPrice - totalDiscounted;
	const totalPrice = showFreeShipping ? totalDiscounted : (totalDiscounted + shippingCost);

	const highestBoxPrice = getHighestBoxPrice(boxes);
	const pricePerMeal = totalPrice / totalWeight;

	return {
		totalPrice, totalBowlsPrice, totalBoxPrice,
		firstOrderDiscount, savings, showFreeShipping,
		highestBoxPrice, pricePerMeal, boxPrice: box?.price
	};
};

export default useCartSummary;